/**
 * <vb-carousel>: the shared ValoraaBead carousel (BRAND-VB.md section 5).
 *
 * Markup contract (see snippets/vb-carousel-controls.liquid for the controls):
 *   <vb-carousel class="vb-carousel" data-mode="center|start" data-autoplay="5000" data-loop>
 *     <div class="vb-carousel__viewport" data-vb-viewport role="region" aria-roledescription="..." aria-label="...">
 *       <ul class="vb-carousel__track" data-vb-track role="list"><li class="vb-carousel__slide" data-vb-slide>...</li></ul>
 *     </div>
 *     {% render 'vb-carousel-controls' %}
 *   </vb-carousel>
 *
 * Native horizontal scrolling with scroll-snap does the moving (touch swipe, trackpad and wheel
 * work as usual). This script adds: the active (centred) slide class for centre mode, an infinite
 * loop with inert clones, arrows, dots, mouse drag, Left/Right keys, autoplay that pauses on
 * hover, focus, off-screen and hidden tabs (never with prefers-reduced-motion), and slide labels.
 */
(() => {
  if (customElements.get('vb-carousel')) return;

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /** @param {string} template @param {Record<string, string|number>} values */
  const fill = (template, values) =>
    Object.entries(values).reduce((text, [key, value]) => text.split(`[${key}]`).join(String(value)), template || '');

  class VbCarousel extends HTMLElement {
    /** @type {HTMLElement[]} */ #items = [];
    /** @type {HTMLElement[]} */ #slides = [];
    #cloneCount = 0;
    #active = -1;
    #timer = 0;
    #scrollRaf = 0;
    #settleTimer = 0;
    #paused = { hover: false, focus: false, hidden: false, offscreen: true, user: false };
    #drag = null;
    #suppressClick = false;
    #abort = new AbortController();
    #resizeObserver = null;
    #intersectionObserver = null;
    /** Last time a person or this script moved the track (ms, performance.now()). */
    #intentAt = 0;
    /** scrollLeft the track should rest at; null until the first placement. */
    #expected = null;

    connectedCallback() {
      if (this.dataset.vbReady) return;
      this.viewport = this.querySelector('[data-vb-viewport]');
      this.track = this.querySelector('[data-vb-track]');
      if (!this.viewport || !this.track) return;
      this.#slides = [...this.track.children].filter((el) => el.hasAttribute('data-vb-slide'));
      if (this.#slides.length === 0) return;
      this.dataset.vbReady = 'true';

      this.mode = this.dataset.mode === 'start' ? 'start' : 'center';
      this.dataset.mode = this.mode;
      this.loop = this.hasAttribute('data-loop') && this.#slides.length > 1;
      this.interval = Math.max(0, parseInt(this.dataset.autoplay || '0', 10) || 0);
      this.controls = this.querySelector('[data-vb-controls]');

      this.#labelSlides();
      if (this.loop) this.#buildClones();
      this.#items = [...this.track.children].filter((el) => el.hasAttribute('data-vb-slide'));
      this.#buildDots();
      this.#bind();

      const start = Math.min(Math.max(parseInt(this.dataset.start || '0', 10) || 0, 0), this.#slides.length - 1);
      requestAnimationFrame(() => {
        this.#scrollToItem(this.#cloneCount + start, false);
        this.#updateActive();
      });
      this.#syncAutoplay();
    }

    disconnectedCallback() {
      this.#abort.abort();
      this.#abort = new AbortController();
      this.#resizeObserver?.disconnect();
      this.#intersectionObserver?.disconnect();
      clearTimeout(this.#timer);
      delete this.dataset.vbReady;
    }

    /** Public API: next / previous / go to a real slide index. */
    next() {
      this.#step(1);
    }

    previous() {
      this.#step(-1);
    }

    goTo(index) {
      this.#scrollToItem(this.#cloneCount + index, true);
    }

    #labelSlides() {
      const total = this.#slides.length;
      const labelTemplate = this.controls?.dataset.slideLabel || '[n] / [total]';
      const roleDescription = this.controls?.dataset.roledescriptionSlide || 'slide';
      if (this.track.getAttribute('role') === 'list') this.track.setAttribute('role', 'none');
      this.#slides.forEach((slide, i) => {
        slide.setAttribute('role', 'group');
        slide.setAttribute('aria-roledescription', roleDescription);
        slide.setAttribute('aria-label', fill(labelTemplate, { n: i + 1, total }));
        slide.dataset.vbIndex = String(i);
      });
    }

    #buildClones() {
      const count = Math.min(this.#slides.length, 5);
      const makeClone = (slide) => {
        const clone = /** @type {HTMLElement} */ (slide.cloneNode(true));
        clone.setAttribute('aria-hidden', 'true');
        clone.removeAttribute('aria-label');
        clone.removeAttribute('role');
        clone.removeAttribute('aria-roledescription');
        clone.inert = true;
        clone.dataset.vbClone = 'true';
        for (const el of [clone, ...clone.querySelectorAll('[id]')]) el.removeAttribute('id');
        for (const el of clone.querySelectorAll('[for], [aria-labelledby], [aria-describedby], [aria-controls]')) {
          el.removeAttribute('for');
          el.removeAttribute('aria-labelledby');
          el.removeAttribute('aria-describedby');
          el.removeAttribute('aria-controls');
        }
        return clone;
      };
      const before = this.#slides.slice(-count).map(makeClone);
      const after = this.#slides.slice(0, count).map(makeClone);
      this.track.prepend(...before);
      this.track.append(...after);
      this.#cloneCount = count;
    }

    #buildDots() {
      const list = this.querySelector('[data-vb-dots]');
      if (!list) return;
      const template = this.controls?.dataset.dotLabel || '[n]';
      list.replaceChildren(
        ...this.#slides.map((_, i) => {
          const li = document.createElement('li');
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'vb-carousel__dot';
          button.setAttribute('aria-label', fill(template, { n: i + 1 }));
          button.addEventListener('click', () => this.goTo(i), { signal: this.#abort.signal });
          li.append(button);
          return li;
        })
      );
      this.dots = [...list.querySelectorAll('button')];
      if (this.#slides.length < 2) list.hidden = true;
    }

    #bind() {
      const { signal } = this.#abort;
      const prev = this.querySelector('[data-vb-prev]');
      const next = this.querySelector('[data-vb-next]');
      prev?.addEventListener('click', () => this.previous(), { signal });
      next?.addEventListener('click', () => this.next(), { signal });
      // One slide: nothing to page through, so no dead arrows in the Tab order.
      if (this.#slides.length < 2) {
        if (prev) prev.hidden = true;
        if (next) next.hidden = true;
      }

      this.viewport.addEventListener('scroll', () => this.#onScroll(), { passive: true, signal });
      // Anything a person does here may scroll the track; scrolls without such intent are the
      // browser re-snapping after a layout change (or a capture tool), and get put back.
      const intent = () => (this.#intentAt = performance.now());
      for (const type of ['pointerdown', 'wheel', 'touchstart', 'keydown']) {
        this.addEventListener(type, intent, { passive: true, signal });
      }
      this.viewport.addEventListener('scrollend', () => this.#settle(), { signal });

      this.addEventListener(
        'keydown',
        (event) => {
          if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
          const target = /** @type {HTMLElement} */ (event.target);
          if (target.closest('input, select, textarea, [contenteditable]')) return;
          event.preventDefault();
          this.#step(event.key === 'ArrowRight' ? 1 : -1);
        },
        { signal }
      );

      // Keyboard users: a focused slide is brought to the centre.
      this.track.addEventListener(
        'focusin',
        (event) => {
          const slide = /** @type {HTMLElement} */ (event.target).closest('[data-vb-slide]');
          if (!slide || this.#drag) return;
          const index = this.#items.indexOf(/** @type {HTMLElement} */ (slide));
          if (index !== this.#active) this.#scrollToItem(index, true);
        },
        { signal }
      );

      // Autoplay pauses while the pointer or focus is inside.
      this.addEventListener('pointerenter', (e) => e.pointerType === 'mouse' && this.#setPaused('hover', true), { signal });
      this.addEventListener('pointerleave', (e) => e.pointerType === 'mouse' && this.#setPaused('hover', false), { signal });
      this.addEventListener('focusin', () => this.#setPaused('focus', true), { signal });
      this.addEventListener('focusout', (e) => {
        if (!this.contains(/** @type {Node} */ (e.relatedTarget))) this.#setPaused('focus', false);
      }, { signal });
      document.addEventListener('visibilitychange', () => this.#setPaused('hidden', document.hidden), { signal });
      reducedMotion.addEventListener('change', () => this.#syncAutoplay(), { signal });

      const pause = this.querySelector('[data-vb-pause]');
      pause?.addEventListener(
        'click',
        () => {
          const next = pause.getAttribute('aria-pressed') !== 'true';
          pause.setAttribute('aria-pressed', String(next));
          this.#setPaused('user', next);
        },
        { signal }
      );

      this.#intersectionObserver = new IntersectionObserver(
        ([entry]) => this.#setPaused('offscreen', !entry?.isIntersecting),
        { threshold: 0.35 }
      );
      this.#intersectionObserver.observe(this);

      this.#resizeObserver = new ResizeObserver(() => {
        if (this.#active >= 0) this.#scrollToItem(this.#active, false);
      });
      this.#resizeObserver.observe(this.viewport);

      this.#bindDrag();
    }

    #bindDrag() {
      const { signal } = this.#abort;
      const vp = this.viewport;
      vp.addEventListener('dragstart', (e) => e.preventDefault(), { signal });
      vp.addEventListener(
        'pointerdown',
        (e) => {
          // Any mouse drags (also on touch-capable laptops, whose primary pointer may report coarse)
          if (e.pointerType !== 'mouse' || e.button !== 0) return;
          this.#drag = { x: e.clientX, left: vp.scrollLeft, moved: false, id: e.pointerId, lastX: e.clientX, v: 0 };
        },
        { signal }
      );
      vp.addEventListener(
        'pointermove',
        (e) => {
          const drag = this.#drag;
          if (!drag || e.pointerId !== drag.id) return;
          const dx = e.clientX - drag.x;
          if (!drag.moved && Math.abs(dx) > 6) {
            drag.moved = true;
            this.dataset.dragging = '';
            vp.setPointerCapture(e.pointerId);
          }
          if (!drag.moved) return;
          drag.v = e.clientX - drag.lastX;
          drag.lastX = e.clientX;
          vp.scrollLeft = drag.left - dx;
        },
        { signal }
      );
      const end = (e) => {
        const drag = this.#drag;
        if (!drag || e.pointerId !== drag.id) return;
        this.#drag = null;
        if (!drag.moved) return;
        delete this.dataset.dragging;
        this.#suppressClick = true;
        // The click that ends a drag must not open the card link
        setTimeout(() => (this.#suppressClick = false), 60);
        const nearest = this.#nearestItem();
        const flick = Math.abs(drag.v) > 4 ? (drag.v < 0 ? 1 : -1) : 0;
        const nearestRect = this.#items[nearest]?.getBoundingClientRect();
        const vpRect = vp.getBoundingClientRect();
        const offCentre = nearestRect ? nearestRect.left + nearestRect.width / 2 - (vpRect.left + vpRect.width / 2) : 0;
        const target = flick && Math.sign(offCentre) !== -flick ? nearest + flick : nearest;
        this.#scrollToItem(this.#clampItem(target), true);
      };
      vp.addEventListener('pointerup', end, { signal });
      vp.addEventListener('pointercancel', end, { signal });
      vp.addEventListener(
        'click',
        (e) => {
          if (!this.#suppressClick) return;
          e.preventDefault();
          e.stopPropagation();
        },
        { capture: true, signal }
      );
    }

    #clampItem(index) {
      return Math.min(Math.max(index, 0), this.#items.length - 1);
    }

    #step(direction) {
      const current = this.#active >= 0 ? this.#active : this.#nearestItem();
      let target = current + direction;
      if (!this.loop) {
        const first = 0;
        const last = this.#items.length - 1;
        if (target > last) target = first;
        if (target < first) target = last;
      }
      this.#scrollToItem(this.#clampItem(target), true);
    }

    /** @param {number} index @param {boolean} smooth */
    #scrollToItem(index, smooth) {
      const item = this.#items[index];
      if (!item) return;
      const vp = this.viewport;
      const vpRect = vp.getBoundingClientRect();
      const rect = item.getBoundingClientRect();
      let left = vp.scrollLeft + rect.left - vpRect.left;
      if (this.mode === 'center') left -= (vpRect.width - rect.width) / 2;
      else left -= parseFloat(getComputedStyle(this.track).paddingInlineStart) || 0;
      this.#intentAt = performance.now();
      this.#expected = Math.min(Math.max(left, 0), vp.scrollWidth - vp.clientWidth);
      vp.scrollTo({ left, behavior: smooth && !reducedMotion.matches ? 'smooth' : 'instant' });
      if (!smooth) this.#updateActive();
      this.#restartTimer();
    }

    #nearestItem() {
      const vpRect = this.viewport.getBoundingClientRect();
      const anchor = this.mode === 'center' ? vpRect.left + vpRect.width / 2 : vpRect.left;
      let best = 0;
      let bestDistance = Infinity;
      this.#items.forEach((item, i) => {
        const rect = item.getBoundingClientRect();
        const point = this.mode === 'center' ? rect.left + rect.width / 2 : rect.left;
        const distance = Math.abs(point - anchor);
        if (distance < bestDistance) {
          bestDistance = distance;
          best = i;
        }
      });
      return best;
    }

    #onScroll() {
      const now = performance.now();
      if (now - this.#intentAt < 600) {
        this.#intentAt = now;
      } else if (this.#expected !== null && !this.#drag && Math.abs(this.viewport.scrollLeft - this.#expected) > 4) {
        // Not marked as intent: the scroll this restore causes lands on #expected and passes.
        this.viewport.scrollTo({ left: this.#expected, behavior: 'instant' });
        return;
      }
      if (!this.#scrollRaf) {
        this.#scrollRaf = requestAnimationFrame(() => {
          this.#scrollRaf = 0;
          this.#updateActive();
        });
      }
      clearTimeout(this.#settleTimer);
      this.#settleTimer = window.setTimeout(() => this.#settle(), 140);
    }

    /** After scrolling stops: leave a clone for its real twin, without animation. */
    #settle() {
      clearTimeout(this.#settleTimer);
      if (this.#drag) return;
      const index = this.#nearestItem();
      if (this.loop) {
        const n = this.#slides.length;
        const k = this.#cloneCount;
        let real = index;
        if (index < k) real = index + n;
        else if (index >= k + n) real = index - n;
        if (real !== index) {
          this.#scrollToItem(real, false);
          return;
        }
      }
      this.#expected = this.viewport.scrollLeft;
      this.#updateActive();
    }

    #updateActive() {
      const index = this.#nearestItem();
      if (index === this.#active) return;
      this.#items[this.#active]?.classList.remove('is-active');
      this.#active = index;
      const item = this.#items[index];
      item?.classList.add('is-active');
      const real = Number(item?.dataset.vbIndex ?? (index - this.#cloneCount + this.#slides.length) % this.#slides.length);
      this.dots?.forEach((dot, i) => dot.setAttribute('aria-current', String(i === real)));
    }

    // ---------- autoplay ----------
    #syncAutoplay() {
      const pause = this.querySelector('[data-vb-pause]');
      const enabled = this.interval > 0 && !reducedMotion.matches && this.#slides.length > 1;
      if (pause) pause.hidden = !enabled;
      this.autoplay = enabled;
      this.#restartTimer();
    }

    #setPaused(reason, value) {
      this.#paused[reason] = value;
      this.#restartTimer();
    }

    #restartTimer() {
      clearTimeout(this.#timer);
      if (!this.autoplay || Object.values(this.#paused).some(Boolean)) return;
      this.#timer = window.setTimeout(() => this.#step(1), this.interval);
    }
  }

  customElements.define('vb-carousel', VbCarousel);
})();
